import { useCallback, useEffect } from "react";
import { useLocation, useHistory } from "react-router";

import getSearchQueryFromObject from "../../utils/getSearchQueryFromObject";
import getObjFromSearchQuery from "../../utils/getObjFromSearchQuery";
import { DEFAULT_PER_PAGE } from "../contexts/GlobalContext";

// The pathname a `pushRoute` is on its way to, module-scope so that an `updateQuery` firing
// from another component while the redirect is in flight does not push over it. Cleared by the
// effect below once that pathname is the current one.
let isRedirecting: string | false = false;

const useRouter = () => {
  const location = useLocation();
  const history = useHistory();
  const { pathname, search } = location;

  const pushRoute = useCallback(
    (route: string, params: Record<string, any> = {}) => {
      // A route may carry its own query ("/users?filter={}"), which wins over `params`.
      const [target, query] = route.split("?");
      const search = route.includes("?") ? getObjFromSearchQuery(query) : params;

      if (target && target !== pathname) {
        isRedirecting = target;

        history.push({
          pathname: target,
          search: getSearchQueryFromObject(search),
        });
      }
    },
    [history, pathname]
  );

  const updateQuery = useCallback(
    (params: Record<string, any> = {}) => {
      const queryObj: Record<string, any> = { ...getObjFromSearchQuery(search) };

      for (let key in params) {
        let value = params[key];

        if (value === Object(value)) {
          value = JSON.stringify(value);
        }

        queryObj[key] = value;
      }

      // make sure perPage is always set
      if (queryObj.pagination && !queryObj.pagination.includes("perPage")) {
        const encoded = JSON.parse(queryObj.pagination);
        queryObj.pagination = JSON.stringify({
          ...encoded,
          perPage: DEFAULT_PER_PAGE,
        });
      }

      const queryString = getSearchQueryFromObject(queryObj);

      if (queryString !== search && !isRedirecting) {
        history.push({
          pathname,
          search: queryString,
        });
      }
    },
    [history, pathname, search]
  );

  useEffect(() => {
    if (isRedirecting && isRedirecting === pathname) {
      isRedirecting = false;
    }
  }, [pathname]);

  return { updateQuery, pushRoute, location, history, pathname };
};

export default useRouter;
